// Lumvio private project calls — pure, dependency-free core logic.
//
// Nothing here touches the network, Cloudflare, or the database. It exists so
// the state machine and preset contract can be unit-tested and shared by the
// UI. Provider identifiers (meeting id, participant id) never appear in this
// module because they never reach the browser.

export type CallMode = "audio" | "video";

export type CallStatus =
  | "provisioning"
  | "ringing"
  | "accepted"
  | "connecting"
  | "active"
  | "ended"
  | "declined"
  | "missed"
  | "failed"
  | "canceled";

export const LIVE_CALL_STATUSES: readonly CallStatus[] = [
  "provisioning",
  "ringing",
  "accepted",
  "connecting",
  "active",
];

export const TERMINAL_CALL_STATUSES: readonly CallStatus[] = [
  "ended",
  "declined",
  "missed",
  "failed",
  "canceled",
];

export const RING_SECONDS = 45;

/** Preset names are fixed; we never fall back to a dashboard default. */
export const AUDIO_PRESET = "lumvio-audio-party";
export const VIDEO_PRESET = "lumvio-video-party";

export function presetForMode(mode: CallMode): string {
  return mode === "audio" ? AUDIO_PRESET : VIDEO_PRESET;
}

export function isLive(status: CallStatus): boolean {
  return LIVE_CALL_STATUSES.includes(status);
}

export function isTerminal(status: CallStatus): boolean {
  return TERMINAL_CALL_STATUSES.includes(status);
}

const ALLOWED_TRANSITIONS: Record<CallStatus, readonly CallStatus[]> = {
  provisioning: ["ringing", "failed", "canceled", "missed"],
  ringing: ["accepted", "declined", "missed", "canceled", "failed"],
  // Accept never means active.
  accepted: ["connecting", "ended", "missed", "failed", "canceled"],
  connecting: ["active", "ended", "failed", "missed"],
  active: ["ended", "failed"],
  ended: [],
  declined: [],
  missed: [],
  failed: [],
  canceled: [],
};

export function canTransition(from: CallStatus, to: CallStatus): boolean {
  return ALLOWED_TRANSITIONS[from].includes(to);
}

/**
 * `active` requires BOTH authorized participants to have actually joined.
 * A single browser asserting it is never enough.
 */
export function shouldBecomeActive(call: {
  caller_joined_at: string | null;
  callee_joined_at: string | null;
}): boolean {
  return !!call.caller_joined_at && !!call.callee_joined_at;
}

/** Duration counts only real conversation time: ended_at - active_at. */
export function computeDurationSeconds(call: {
  active_at: string | null;
  ended_at: string | null;
}): number {
  if (!call.active_at || !call.ended_at) return 0;
  const started = Date.parse(call.active_at);
  const ended = Date.parse(call.ended_at);
  if (!Number.isFinite(started) || !Number.isFinite(ended)) return 0;
  return Math.max(0, Math.floor((ended - started) / 1000));
}

export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const rest = s % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    return `${h}:${String(m % 60).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
  }
  return `${m}:${String(rest).padStart(2, "0")}`;
}

export function isRingExpired(ringExpiresAt: string, now: number = Date.now()): boolean {
  const at = Date.parse(ringExpiresAt);
  return Number.isFinite(at) ? at <= now : true;
}

/** Media the SDK is allowed to request when joining. */
export function initialMediaForMode(mode: CallMode): { audio: boolean; video: boolean } {
  return { audio: true, video: mode === "video" };
}

export type CleanupResult = "done" | "retry_required" | "not_required" | "gone";

/** Only coarse cleanup outcomes are ever persisted — never provider payloads. */
export function classifyCleanup(input: { ok: boolean; status: number }): CleanupResult {
  if (input.ok) return "done";
  if (input.status === 404 || input.status === 410) return "gone";
  return "retry_required";
}

/** True when the provider still owes us a confirmed teardown. */
export function needsCleanupRetry(result: CleanupResult | null | undefined): boolean {
  return result === "retry_required";
}

export interface CallRow {
  id: string;
  project_id: string;
  initiator_user_id: string;
  callee_user_id: string;
  mode: CallMode;
  status: CallStatus;
  ring_expires_at: string;
  active_at: string | null;
  ended_at: string | null;
  duration_seconds: number | null;
  created_at: string;
}

/** Defence in depth: strip anything provider-shaped before it hits React. */
const FORBIDDEN_KEYS = [
  "realtimekit_meeting_id",
  "meeting_id",
  "caller_participant_id",
  "callee_participant_id",
  "participant_id",
  "auth_token",
  "authToken",
];

export function stripProviderFields<T extends Record<string, unknown>>(row: T): T {
  const out = { ...row };
  for (const key of FORBIDDEN_KEYS) delete out[key];
  return out;
}

export function hasProviderFields(row: Record<string, unknown>): boolean {
  return FORBIDDEN_KEYS.some((k) => k in row);
}
